import { type AxiosResponse, isAxiosError } from "axios";

export type CustomAxiosError = {
	code: number;
	message: string;
};

export async function axiosExtract<T>(axiosPromise: Promise<AxiosResponse<T>>): Promise<T> {
	const { data } = await axiosPromise;
	return data;
}

// FileReader reads both a browser's Blob and the jsdom Blob the tests hand back; Blob.text() reads only the first.
function readBlobAsText(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === "string") {
				resolve(reader.result);
			} else {
				reject(new Error("The response was not read as text"));
			}
		};
		reader.onerror = () => reject(reader.error ?? new Error("Could not read the response"));
		reader.readAsText(blob);
	});
}

/** The message of a 400 the backend refused with, read from a JSON body or a JSON Blob (a `responseType: "blob"` request). */
export async function backendErrorMessage(error: unknown): Promise<string | undefined> {
	if (!isAxiosError<unknown>(error) || error.response?.status !== 400) {
		return undefined;
	}
	let body: unknown = error.response.data;
	if (body instanceof Blob) {
		try {
			body = JSON.parse(await readBlobAsText(body));
		} catch {
			return undefined;
		}
	}
	const message = body != null && typeof body === "object" && "message" in body ? body.message : undefined;
	return typeof message === "string" && message ? message : undefined;
}

export function isAxiosResponse(response: unknown): response is AxiosResponse {
	return (
		response != null &&
		typeof response === "object" &&
		"data" in response &&
		"status" in response &&
		"statusText" in response &&
		"headers" in response &&
		"config" in response
	);
}
